import { create } from "zustand"

/**
 * Download progress for the drawing the quotation viewer is opening.
 *
 * In a store rather than in `useQuotationViewer` local state for one reason: the orchestrator is a
 * ~1,600-line hook, and progress arrives once per network chunk — thousands of times for a large
 * drawing. Setting state there would re-run every memo and effect in it on each chunk. Only the
 * overlay needs to re-render, so only the overlay subscribes here. `useQuotationViewer` is the
 * single writer.
 *
 * Why this exists at all: the viewer used to get its grey overlay and percentage from Apryse, which
 * showed them while IT downloaded the file via `initialDoc`. That download was redundant — the
 * orchestrator fetched the same file again and replaced it with a Blob — so removing it also
 * removed the only progress indicator, leaving a silent wait on a file that can reach 120 MB. We do
 * the fetch, so we report the progress. Apryse 11.9.1 exposes no public API to drive its own
 * loading modal (`setLoadingProgress` exists in the bundle but not in the typed surface).
 */
type Store = {
	/** True from the moment a download is known to be slow enough to be worth announcing. */
	isLoading: boolean
	/**
	 * 0..1 when the response declared a `Content-Length`, otherwise null — the download is running
	 * but its size is unknown, so the overlay shows a spinner with no percentage.
	 */
	fraction: number | null
	start: () => void
	report: (fraction: number | null) => void
	finish: () => void
}

export const useDocumentLoadStore = create<Store>((set) => ({
	isLoading: false,
	fraction: null,
	start: () => set({ isLoading: true, fraction: null }),
	report: (fraction) => set({ isLoading: true, fraction }),
	finish: () => set({ isLoading: false, fraction: null }),
}))
